import { useRef, useEffect } from 'react';

/**
 * MasterTimeline — one bar spanning every branch on the master path.
 *
 * It is NOT a clock. There are no seconds and no time axis anywhere on it.
 * The bar is divided into one EQUAL segment per branch (root → highest-priority
 * child → …), and the handle's position is a plain number in [0, branchCount]:
 *
 *     0       1       2       3
 *     |-------|-------|-------|
 *     root    kick    finish  ...
 *
 * Pulling the handle moves the whole scenario: the branch under the handle is
 * loaded, the scenario tree follows it, and the playhead inside that branch is
 * `fraction × that branch's own timeline length` — so branches of different
 * lengths all map onto the same sized segment.
 *
 * The handle is derived from the live playhead, so scrubbing the normal
 * timeline or playing a branch moves this bar automatically, and dragging this
 * bar moves the timeline. One position, two views of it.
 *
 * Switching branch by hand does the same thing in reverse: the playhead's
 * fraction of the timeline is carried into the branch being opened (see
 * playheadFraction), so the handle keeps its place INSIDE the new segment
 * instead of snapping back to that segment's start.
 */
/**
 * Double-tap / double-click detection for the master bar: when `last`
 * ({ t, i }) is a tap on the SAME segment within 400 ms of `now`, returns
 * that segment index; otherwise null. `pos` is the master position (0..n).
 * Touch AND mouse — "play the path from HERE" should not be a touch-only
 * verb; the segment must match, so precise double-click scrubbing across a
 * boundary never fires it.
 */
export function masterDoubleTap(e, pos, n, last, now) {
    if (!e || (e.pointerType !== 'touch' && e.pointerType !== 'mouse')) return null;
    const i = Math.min(Math.max(0, Math.floor(pos)), Math.max(0, n - 1));
    if (last && last.i === i && now - last.t < 400) return i;
    return null;
}

export default function MasterTimeline({
    chain = [],
    forkAlts = null, // { branchId: N } — this chain step passes a fork with N other ways (the "why did it play THIS?" answer)
    chosenIds = null, // Set of branch ids the user picked this session — a fork badge says YOUR PICK or DEFAULT
    pos = 0,
    bottom = 0,
    height = 28,
    playing = false,
    loadingLabel = '',
    onScrub = () => {},
    onSegmentDoubleTap = null, // (branchId, shiftKey) => void — double-tap/click plays the path from there; ⇧ = just that branch
    t = (k) => k,
}) {
    const barRef = useRef(null);
    const draggingRef = useRef(false);
    const lastTapRef = useRef({ t: 0, i: -1 });
    // HOLD A SEGMENT (touch) = play JUST that branch — the finger twin of
    // ⇧+double-click. The bar has no modifier a thumb can press, so without this
    // the narrower choice, the one your finger is actually pointing at, had no
    // door on a phone at all.
    const holdRef = useRef(null);
    const holdXRef = useRef(0);
    const cancelHold = () => {
        if (holdRef.current) { clearTimeout(holdRef.current); holdRef.current = null; }
    };
    // The bar can unmount mid-press (the scenario changed under it). A timer that
    // outlives the component would start a playback nobody asked for.
    useEffect(() => cancelHold, []); // eslint-disable-line react-hooks/exhaustive-deps

    const n = Math.max(1, chain.length);
    const activeIndex = Math.min(n - 1, Math.max(0, Math.floor(pos)));
    const frac = Math.min(1, Math.max(0, pos - activeIndex));

    /** Screen x → master position (0..n). */
    const posFromEvent = (clientX) => {
        const el = barRef.current;
        if (!el) return 0;
        const r = el.getBoundingClientRect();
        if (r.width <= 0) return 0;
        const ratio = (clientX - r.left) / r.width;
        return Math.min(n, Math.max(0, ratio * n));
    };

    const startDrag = (e) => {
        draggingRef.current = true;
        try { barRef.current.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
        const p = posFromEvent(e.clientX);
        onScrub(p);
        // DOUBLE-TAP / DOUBLE-CLICK on the same segment = play the path from
        // there — the pointer twin of Space. The first tap already scrubbed +
        // selected.
        const dt = masterDoubleTap(e, p, n, lastTapRef.current, performance.now());
        if (dt !== null) {
            lastTapRef.current = { t: 0, i: -1 };
            // ⇧ while double-clicking = "just this branch" (the mouse twin of
            // ⇧Space play-to-end); plain = walk on through the path.
            if (onSegmentDoubleTap) onSegmentDoubleTap(chain[dt] && chain[dt].id, e.shiftKey);
        } else {
            lastTapRef.current = { t: performance.now(), i: Math.min(n - 1, Math.max(0, Math.floor(p))) };
            // Arm the hold only on a finger, and only when this press did NOT
            // complete a double-tap. Same 550 ms the pitch and the strip already
            // use, so the app has one feel for "press and wait".
            if (e.pointerType === 'touch' && onSegmentDoubleTap) {
                const idx = Math.min(n - 1, Math.max(0, Math.floor(p)));
                holdXRef.current = e.clientX;
                cancelHold();
                holdRef.current = setTimeout(() => {
                    holdRef.current = null;
                    lastTapRef.current = { t: 0, i: -1 };
                    onSegmentDoubleTap(chain[idx] && chain[idx].id, true);
                }, 550);
            }
        }
        e.preventDefault();
    };

    const onMove = (e) => {
        if (!draggingRef.current) return;
        // A drag that actually walks the bar cancels the hold: pulling the handle
        // is a different verb, and a menu that pops mid-drag is the bug this app
        // has already learned to avoid on the pitch.
        if (holdRef.current && Math.abs(e.clientX - holdXRef.current) > 8) cancelHold();
        onScrub(posFromEvent(e.clientX));
    };

    const endDrag = () => { draggingRef.current = false; cancelHold(); };

    return (
        <div
            ref={barRef}
            className="v3-master-bar"
            role="slider"
            aria-label={t('master.title')}
            aria-valuemin={0}
            aria-valuemax={n}
            aria-valuenow={Number(pos.toFixed(3))}
            aria-valuetext={`${chain[activeIndex]?.name || ''} ${Math.round(frac * 100)}%`}
            title={t('master.hint')}
            onPointerDown={startDrag}
            onPointerMove={onMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            style={{
                position: 'absolute', left: 0, right: 0, bottom, height,
                zIndex: 56, display: 'flex', alignItems: 'stretch',
                background: 'rgba(2,6,23,0.96)',
                borderTop: '1px solid #334155', borderBottom: '1px solid #334155',
                touchAction: 'none', userSelect: 'none', cursor: 'ew-resize',
                overflow: 'hidden',
            }}
        >
            {/* One equal segment per branch — labelled with branch names, never times */}
            {chain.map((b, i) => {
                const isActive = i === activeIndex;
                return (
                    <div
                        key={b.id}
                        style={{
                            flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: 5, padding: '0 6px',
                            borderLeft: i === 0 ? 'none' : '1px solid rgba(51,65,85,0.8)',
                            background: isActive
                                ? 'rgba(99,102,241,0.30)'
                                : (i < activeIndex ? 'rgba(15,23,42,0.9)' : 'rgba(2,6,23,0.6)'),
                            overflow: 'hidden',
                        }}
                    >
                        <span style={{
                            flexShrink: 0, fontSize: 9, fontWeight: 800,
                            color: isActive ? '#c7d2fe' : '#475569',
                        }}>{i + 1}</span>
                        <span title={b.name} style={{
                            minWidth: 0, fontSize: 10.5, fontWeight: isActive ? 800 : 600,
                            color: isActive ? '#e0e7ff' : '#94a3b8',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{b.name}</span>
                        {forkAlts && forkAlts[b.id] ? (
                            // The badge carries TWO facts now, because the
                            // tutorial's most surprising rule is invisible:
                            // picks live in THIS session only and are never
                            // saved, so a reloaded scenario silently falls back
                            // to the order-1 path. Amber ⑂N✓ = the way YOU
                            // chose; grey ⑂N = the default you never chose.
                            (() => {
                                const picked = !!(chosenIds && chosenIds.has(b.id));
                                return (
                                    <span
                                        title={picked ? t('master.yourPick') : t('master.defaultPick')}
                                        style={{
                                            flexShrink: 0, fontSize: 9, fontWeight: 800,
                                            color: picked ? '#fbbf24' : '#64748b',
                                            background: picked ? 'rgba(251,191,36,0.12)' : 'rgba(100,116,139,0.12)',
                                            border: `1px solid ${picked ? 'rgba(251,191,36,0.4)' : 'rgba(100,116,139,0.35)'}`,
                                            borderRadius: 4, padding: '0 4px',
                                        }}
                                    >⑂{forkAlts[b.id]}{picked ? '✓' : ''}</span>
                                );
                            })()
                        ) : null}
                    </div>
                );
            })}

            {/* Handle — derived from the live playhead, not stored */}
            <div style={{
                position: 'absolute', top: 0, bottom: 0, left: 0, width: '100%',
                pointerEvents: 'none',
            }}>
                <div style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${(pos / n) * 100}%`, width: 2, marginLeft: -1,
                    background: '#f8fafc', boxShadow: '0 0 6px rgba(248,250,252,0.55)',
                }} />
                <div style={{
                    position: 'absolute', top: '50%', left: `${(pos / n) * 100}%`,
                    width: 11, height: 11, marginLeft: -5.5, marginTop: -5.5,
                    borderRadius: '50%', background: playing ? '#22c55e' : '#f8fafc',
                    border: '1px solid #0b1120',
                }} />
                {loadingLabel && playing && (
                    <div style={{
                        position: 'absolute', right: 6, top: 1, fontSize: 9.5,
                        color: '#bbf7d0', fontWeight: 800,
                    }}>{loadingLabel}</div>
                )}
            </div>
        </div>
    );
}
